import type { StatisticSectionRow } from '../../StatisticsV2/shared/StatisticsSectionsTable/types';
import type { InboundStatistic } from '../../../store/reducers/IncomingStatisticsSlice';

import {
  createIncomingMetricRow as createMetricRow,
  createIncomingSectionRow as createSectionRow,
} from './incomingStatisticRowFactory';
import { getSelectedScenarioVariant } from '../../../models/Task/scenarioMapping';

export type InboundStatisticRow = StatisticSectionRow;

const COMMON_SECTION = 'Общие показатели';
const INTERACTION_SECTION = 'Результаты взаимодействия';
const FAILED_REASONS_SECTION = 'Причины неудачных взаимодействий';
const FAILED_REASONS_SUBTITLE = '(Не удалось получить результат)';

const buildCommonRows = (
  statistic: InboundStatistic,
  percentDecimals = 2,
): InboundStatisticRow[] => [
  createMetricRow({
    key: 'common-totalCount',
    section: COMMON_SECTION,
    parameter: 'Всего обращений',
    numerator: statistic.totalCount,
    denominator: statistic.totalCount,
    percentDecimals,
  }),
  createMetricRow({
    key: 'common-primaryCount',
    section: COMMON_SECTION,
    parameter: 'Доля первичных обращений',
    numerator: statistic.primaryCount,
    denominator: statistic.totalCount,
    percentDecimals,
  }),
];

const buildInteractionRows = (
  statistic: InboundStatistic,
): InboundStatisticRow[] => [
  createMetricRow({
    key: 'interaction-confirmedTasksCount',
    section: INTERACTION_SECTION,
    parameter: 'Успешно записалось на прием',
    numerator: statistic.confirmedTasksCount,
    denominator: statistic.totalCount,
  }),
  createMetricRow({
    key: 'interaction-cancelledTasksCount',
    section: INTERACTION_SECTION,
    parameter: 'Отказалось от записи на прием',
    numerator: statistic.cancelledTasksCount,
    denominator: statistic.totalCount,
  }),
  createMetricRow({
    key: 'interaction-waitingListTasksCount',
    section: INTERACTION_SECTION,
    parameter: 'Записалось в лист ожидания',
    numerator: statistic.waitingListTasksCount,
    denominator: statistic.totalCount,
  }),
  createMetricRow({
    key: 'interaction-operatorHandedTasksCount',
    section: INTERACTION_SECTION,
    parameter: 'Передано оператору',
    numerator: statistic.operatorHandedTasksCount,
    denominator: statistic.totalCount,
  }),
  createMetricRow({
    key: 'interaction-notIdentifiedTasksCount',
    section: INTERACTION_SECTION,
    parameter: 'Не удалось продолжить диалог - абонент не идентифицирован',
    numerator: statistic.notIdentifiedTasksCount,
    denominator: statistic.totalCount,
  }),
  createMetricRow({
    key: 'interaction-notReceivedTasksCount',
    section: INTERACTION_SECTION,
    parameter: 'Не удалось получить результат',
    numerator: statistic.notReceivedTasksCount,
    denominator: statistic.totalCount,
  }),
];

const buildInformingInteractionRows = (
  statistic: InboundStatistic,
  percentDecimals = 1,
): InboundStatisticRow[] => [
  createMetricRow({
    key: 'interaction-confirmedTasksCount',
    section: INTERACTION_SECTION,
    parameter: 'Информация прослушана',
    numerator: statistic.confirmedTasksCount,
    denominator: statistic.totalCount,
    percentDecimals,
  }),
  createMetricRow({
    key: 'interaction-notReceivedTasksCount',
    section: INTERACTION_SECTION,
    parameter: 'Информация не прослушана (не удалось получить результат)',
    numerator: statistic.notReceivedTasksCount,
    denominator: statistic.totalCount,
    percentDecimals,
  }),
];

const buildFailedReasonRows = (
  statistic: InboundStatistic,
  percentDecimals = 2,
): InboundStatisticRow[] => {
  const notRecognizedCount = Math.max(
    statistic.notReceivedTasksCount -
      statistic.errorCount -
      statistic.hangUpCount,
    0,
  );

  return [
    createMetricRow({
      key: 'failedReasons-errorCount',
      section: FAILED_REASONS_SECTION,
      parameter: 'Ошибка',
      numerator: statistic.errorCount,
      denominator: statistic.totalCount,
      percentDecimals,
    }),
    createMetricRow({
      key: 'failedReasons-hangUpCount',
      section: FAILED_REASONS_SECTION,
      parameter: 'Сброс звонка абонентом',
      numerator: statistic.hangUpCount,
      denominator: statistic.totalCount,
      percentDecimals,
    }),
    createMetricRow({
      key: 'failedReasons-notRecognizedCount',
      section: FAILED_REASONS_SECTION,
      parameter: 'Не распознана речь',
      numerator: notRecognizedCount,
      denominator: statistic.totalCount,
      percentDecimals,
    }),
  ];
};

export const buildIncomingStatisticRows = (
  statistic: InboundStatistic,
  selectedScenario: string | null,
): InboundStatisticRow[] => {
  if (!statistic || typeof statistic !== 'object') {
    return [];
  }

  const variant = getSelectedScenarioVariant(selectedScenario);
  const percentDecimals = variant === 'informing' ? 1 : 2;
  const rows: InboundStatisticRow[] = [
    createSectionRow(
      'common',
      COMMON_SECTION,
      buildCommonRows(statistic, percentDecimals),
    ),
  ];

  if (variant === 'informing') {
    return [
      ...rows,
      createSectionRow(
        'interaction',
        INTERACTION_SECTION,
        buildInformingInteractionRows(statistic, percentDecimals),
      ),
      createSectionRow(
        'failedReasons',
        FAILED_REASONS_SECTION,
        buildFailedReasonRows(statistic, percentDecimals),
        FAILED_REASONS_SUBTITLE,
      ),
    ];
  }

  if (variant !== 'appointment') {
    return rows;
  }

  return [
    ...rows,
    createSectionRow(
      'interaction',
      INTERACTION_SECTION,
      buildInteractionRows(statistic),
    ),
    createSectionRow(
      'failedReasons',
      FAILED_REASONS_SECTION,
      buildFailedReasonRows(statistic),
      FAILED_REASONS_SUBTITLE,
    ),
  ];
};
